/**
 * Service for Places Avis (Reviews) API
 * Manages reviews/ratings for places
 */

import { getUser } from './authService';

const API_BASE_URL = 'https://apicabaza.creago.be/rest/cabaza/places_avis';

/**
 * Map of lowercase keys to camelCase
 */
const keyMapping = {
    peopleid: 'peopleId',
    placeuuid: 'placeUuid',
    createdat: 'createdAt',
    updatedat: 'updatedAt',
    firstname: 'firstName',
    lastname: 'lastName',
    peopleuuid: 'peopleUuid',
    peopleimgurl: 'peopleImgUrl',
    totalreviews: 'totalReviews',
    totalstars: 'totalStars',
    averagerating: 'averageRating',
    ratingdistribution: 'ratingDistribution'
};

/**
 * Normalize object keys to camelCase
 */
const normalizeKeys = (obj) => {
    if (obj === null || obj === undefined) return obj;
    if (Array.isArray(obj)) {
        return obj.map(item => normalizeKeys(item));
    }
    if (typeof obj === 'object') {
        return Object.fromEntries(
            Object.entries(obj).map(([key, value]) => {
                const lowerKey = key.toLowerCase();
                const newKey = keyMapping[lowerKey] || lowerKey;
                return [newKey, normalizeKeys(value)];
            })
        );
    }
    return obj;
};

/**
 * Get reviews for a place
 * @param {string} placeUuid - Place UUID
 * @param {Object} options - Query options (orderBy, orderDir)
 * @returns {Promise<Array>} Reviews array
 */
export const getPlaceReviews = async (placeUuid, options = {}) => {
    try {
        const { orderBy = 'createdAt', orderDir = 'DESC' } = options;

        const params = new URLSearchParams({
            placeUuid: placeUuid,
            orderBy,
            orderDir
        });

        const response = await fetch(`${API_BASE_URL}?${params}`, {
            method: 'GET',
            credentials: 'include'
        });

        const result = await response.json();
        return normalizeKeys(result.data || result.DATA || []);
    } catch (error) {
        console.error('Error fetching place reviews:', error);
        throw error;
    }
};

/**
 * Get a single review by ID
 * @param {number} avisId - Review ID
 * @returns {Promise<Object>} Review object
 */
export const getReviewById = async (avisId) => {
    try {
        const params = new URLSearchParams({
            avisId: avisId.toString()
        });

        const response = await fetch(`${API_BASE_URL}?${params}`, {
            method: 'GET',
            credentials: 'include'
        });

        const result = await response.json();
        const reviews = normalizeKeys(result.data || result.DATA || []);
        return reviews.length > 0 ? reviews[0] : null;
    } catch (error) {
        console.error('Error fetching review:', error);
        throw error;
    }
};

/**
 * Get current user's reviews
 * @returns {Promise<Array>} User's reviews
 */
export const getMyReviews = async () => {
    try {
        const user = getUser();
        if (!user?.id) {
            console.error('No user logged in');
            return [];
        }

        const params = new URLSearchParams({
            peopleId: user.id.toString()
        });

        const response = await fetch(`${API_BASE_URL}/my?${params}`, {
            method: 'GET',
            credentials: 'include'
        });

        const result = await response.json();
        return normalizeKeys(result.data || result.DATA || []);
    } catch (error) {
        console.error('Error fetching my reviews:', error);
        throw error;
    }
};

/**
 * Get statistics for a place (average rating, distribution)
 * @param {string} placeUuid - Place UUID
 * @returns {Promise<Object>} Stats object
 */
export const getPlaceStats = async (placeUuid) => {
    try {
        const response = await fetch(`${API_BASE_URL}/stats/${placeUuid}`, {
            method: 'GET',
            credentials: 'include'
        });

        const result = await response.json();
        return normalizeKeys(result.data || result.DATA || {
            totalReviews: 0,
            totalStars: 0,
            averageRating: 0,
            ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
        });
    } catch (error) {
        console.error('Error fetching place stats:', error);
        return {
            totalReviews: 0,
            totalStars: 0,
            averageRating: 0,
            ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
        };
    }
};

/**
 * Create a new review
 * @param {string} placeUuid - Place UUID
 * @param {number} rating - Rating (1-5)
 * @param {string} comment - Optional comment
 * @returns {Promise<Object>} Result with created review
 */
export const createReview = async (placeUuid, rating, comment = '') => {
    try {
        const user = getUser();
        if (!user?.id) {
            return {
                success: false,
                message: 'Vous devez être connecté pour donner un avis'
            };
        }

        const response = await fetch(API_BASE_URL, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ placeUuid, rating, comment, peopleId: user.id })
        });

        // Check if response is JSON
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
            console.error('API returned non-JSON response:', response.status);
            return {
                success: false,
                message: 'Service avis non disponible. Veuillez réessayer plus tard.'
            };
        }

        const text = await response.text();
        if (!text || text.trim() === '') {
            return {
                success: false,
                message: 'Service avis non disponible. Veuillez réessayer plus tard.'
            };
        }

        const result = JSON.parse(text);

        if (!response.ok) {
            // Check for duplicate (409 Conflict)
            if (response.status === 409) {
                return {
                    success: false,
                    duplicate: true,
                    existingId: result.existingId || result.EXISTINGID,
                    message: result.message || result.MESSAGE || 'Vous avez déjà donné un avis pour cet endroit'
                };
            }
            throw new Error(result.message || result.MESSAGE || 'Error creating review');
        }

        return {
            success: true,
            data: normalizeKeys(result.data || result.DATA),
            message: result.message || result.MESSAGE
        };
    } catch (error) {
        console.error('Error creating review:', error);
        return {
            success: false,
            message: error.message === 'Unexpected end of JSON input'
                ? 'Service avis non disponible. Veuillez réessayer plus tard.'
                : error.message
        };
    }
};

/**
 * Update an existing review
 * @param {number} avisId - Review ID
 * @param {Object} updates - Fields to update (rating, comment)
 * @returns {Promise<Object>} Result with updated review
 */
export const updateReview = async (avisId, updates) => {
    try {
        const user = getUser();
        if (!user?.id) {
            return {
                success: false,
                message: 'Vous devez être connecté pour modifier un avis'
            };
        }

        const response = await fetch(`${API_BASE_URL}/${avisId}`, {
            method: 'PUT',
            credentials: 'include',
            body: JSON.stringify({ ...updates, peopleId: user.id })
        });

        const result = await response.json();

        if (!response.ok) {
            throw new Error(result.message || result.MESSAGE || 'Error updating review');
        }

        return {
            success: true,
            data: normalizeKeys(result.data || result.DATA),
            message: result.message || result.MESSAGE
        };
    } catch (error) {
        console.error('Error updating review:', error);
        return {
            success: false,
            message: error.message
        };
    }
};

/**
 * Delete a review
 * @param {number} avisId - Review ID
 * @returns {Promise<Object>} Result
 */
export const deleteReview = async (avisId) => {
    try {
        const user = getUser();
        if (!user?.id) {
            return {
                success: false,
                message: 'Vous devez être connecté pour supprimer un avis'
            };
        }

        const params = new URLSearchParams({
            peopleId: user.id.toString()
        });

        const response = await fetch(`${API_BASE_URL}/${avisId}?${params}`, {
            method: 'DELETE',
            credentials: 'include'
        });

        const result = await response.json();

        return {
            success: response.ok,
            message: result.message || result.MESSAGE
        };
    } catch (error) {
        console.error('Error deleting review:', error);
        return {
            success: false,
            message: error.message
        };
    }
};

/**
 * Check if user has already reviewed a place
 * @param {string} placeUuid - Place UUID
 * @returns {Promise<Object|null>} Existing review or null
 */
export const getUserReviewForPlace = async (placeUuid) => {
    try {
        const myReviews = await getMyReviews();
        return myReviews.find(r => r.placeUuid === placeUuid) || null;
    } catch (error) {
        console.error('Error checking user review:', error);
        return null;
    }
};

export default {
    getPlaceReviews,
    getReviewById,
    getMyReviews,
    getPlaceStats,
    createReview,
    updateReview,
    deleteReview,
    getUserReviewForPlace
};
